/**
 * Browser view: KasmVNC (port 6901) served through code-server's
 * authenticated proxy, embedded next to the IDE/terminal views. Opening the
 * view starts the browser in the pod (useBrowserSession).
 */

import { type ReactElement } from 'react';
import { useBrowserSession } from '../hooks/useBrowserSession';

interface BrowserViewProps {
  workspaceId: string;
  workspaceUrl: string;
}

// KasmVNC's client treats being framed as "embedded in Kasm Workspaces": it
// sets resize=off (the desktop never follows the frame, so it draws clipped
// with black unpainted areas), turns the clipboard and WebP off and hides
// its control bar, expecting a Kasm parent page to drive it. show_control_bar
// opts out of all of that, so the frame behaves exactly like a new tab.
const FRAME_QUERY = '?show_control_bar=true';

export function BrowserView({ workspaceId, workspaceUrl }: BrowserViewProps): ReactElement {
  const src = `${workspaceUrl}proxy/6901/`;
  const { session, retry } = useBrowserSession(workspaceId, src);

  return (
    <>
      <div className="workspace-page__browser-bar">
        <a href={src} target="_blank" rel="noopener" className="workspace-page__link">
          Open in new tab
        </a>
      </div>
      {session.phase === 'running' ? (
        <iframe
          src={`${src}${FRAME_QUERY}`}
          title="Workspace browser"
          className="workspace-page__iframe"
          allow="clipboard-read *; clipboard-write *"
        />
      ) : (
        <div className="workspace-page__centered" role="status">
          {session.phase === 'failed' ? (
            <>
              <p className="workspace-page__subtitle">
                The browser didn&apos;t start: {session.error}
              </p>
              <button type="button" className="workspace-page__button" onClick={retry}>
                Try again
              </button>
            </>
          ) : (
            <p className="workspace-page__subtitle">
              {session.phase === 'starting' ? 'Starting the browser…' : 'Connecting to the browser…'}
            </p>
          )}
        </div>
      )}
    </>
  );
}
